import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DangerAlert from "../child/Alert/DengerAlert";
import Loader from "../child/Loder";
import Multiselect from "multiselect-react-dropdown";
import { fetchAvailableCities } from "../services/analyticsService";

// A saved "filtered" audience that is only a pocket (and its readiness)
// opens as One pocket.
const POCKET_ONLY_OTHER_FILTERS = ["gender", "interestedIn", "ageRange", "subscription", "boosted", "location"];
const onlyPocket = (filters) =>
  !!(filters && filters.pocketId) &&
  !POCKET_ONLY_OTHER_FILTERS.some((k) => {
    const v = filters[k];
    return Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== "";
  });

const pocketName = (options, id) => {
  const p = (options || []).find((o) => String(o.id) === String(id));
  return p ? p.name : "pocket";
};

const formatEastern = (when) =>
  new Date(when).toLocaleString("en-US", {
    timeZone: "America/Toronto",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const SendNotificationNew = () => {
  const { id } = useParams(); // id = notificationId for edit mode
  const isEditMode = !!id;
  const navigate = useNavigate();
  const authToken = localStorage.getItem("authToken");
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [selectType, setSelectType] = useState(""); // "all" | "Pocket" | "SelectedUser" | "Filtered"
  const [selectUsers, setSelectUsers] = useState([]); // IDs array
  const [users, setUsersData] = useState([]);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [actionType, setActionType] = useState("update"); // "update" | "updateAndSend"


  // filter states
  const [gender, setGender] = useState([]); // e.g. ["male"]
  const [interestedIn, setInterestedIn] = useState([]); // e.g. ["female"]
  const [minAge, setMinAge] = useState("");
  const [maxAge, setMaxAge] = useState("");
  const [noUserMessage, setNoUserMessage] = useState("");
  const [subscription, setSubscription] = useState(""); // "" | "active" | "premium"
  const [boosted, setBoosted] = useState(""); // "" | "true" | "false"
  const [sentType, setSentType] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleList, setScheduleList] = useState([
    {
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
    },
  ]);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [location, setLocation] = useState("");
  const [cityOptions, setCityOptions] = useState([]);
  // Pocket targeting (founder ask 2026-08-27): reach a launch city in one
  // pick instead of chip-selecting every member, and slice it by whether
  // members can actually be introduced yet.
  const [pocketId, setPocketId] = useState("");
  const [readiness, setReadiness] = useState("");
  const [pocketOptions, setPocketOptions] = useState([]);
  // Automatic city notice (founder 2026-11-02): "Almost time ✨" or "{City}
  // is live 🎉", sent once by the server to one city's members. Shown read
  // only; the server refuses Update, Update & Send, Resend and Delete on it.
  const [cityNotice, setCityNotice] = useState(null);
  const [sentAt, setSentAt] = useState(null);

  // dropdown options (adjust as needed)
  const genderOptions = [
    { name: "Man", id: "man" },
    { name: "Woman", id: "woman" },
    { name: "Beyond Binary", id: "beyond_binary" },
  ];

  const interestedOptions = [
    { name: "Men", id: "men" },
    { name: "Women", id: "women" },
    { name: "Beyond Binary", id: "beyond_binary" },
    { name: "Everyone", id: "every_one" },
  ];




  useEffect(() => {
    if (!isEditMode) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // 1️⃣ Fetch notification
        const notifRes = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/viewSentNotificationAdmin/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const notifData = await notifRes.json();
        if (!notifRes.ok) throw new Error(notifData.message);

        const notification = notifData.notification;

        setTitle(notification.title || "");
        setDescription(notification.message || "");
        setSentType(notification.sentType || "");
        setCityNotice(notification.cityNotice || null);
        setSentAt(notification.sentAt || null);

        // Audience mode (2026-08-27): the server stores it; legacy docs
        // are inferred server-side. Editing a one-user test used to open
        // preloaded with every member because the mode was guessed here
        // and a second effect then force-selected everyone.
        const mode =
          notification.audienceType ||
          (notification.filters
            ? "filtered"
            : notification.targetUsers?.length
              ? "selected"
              : "all");

        if (mode === "filtered" && onlyPocket(notification.filters)) {
          setSelectType("Pocket");
          setUsersData([]);
          setSelectUsers([]);
        } else if (mode === "filtered") {
          setSelectType("Filtered");
          setUsersData([]);
          setSelectUsers([]);
        } else if (mode === "selected") {
          setSelectType("SelectedUser");
          // 2️⃣ Fetch all users so the saved selection can be shown/edited
          const usersRes = await fetch(`${process.env.REACT_APP_BASE_URL}/admin/notificationUserList?all=true`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const usersData = await usersRes.json();
          const list = usersData?.users || [];

          // Merge with targetUsers to ensure all selected users exist
          const mergedUsers = [
            ...list,
            ...(notification.targetUsers || []).filter(
              (u) => !list.some((user) => user._id === u._id)
            ),
          ];
          setUsersData(mergedUsers);

          // 3️⃣ Restore exactly the saved selection
          const targetIds = (notification.targetUsers || []).map((u) => u._id);
          setSelectUsers(targetIds);
        } else {
          // All Users is a mode, never a materialized list of chips.
          setSelectType("all");
          setUsersData([]);
          setSelectUsers([]);
        }

        // 4️⃣ Set schedule (convert ISO date to YYYY-MM-DD)
        if (notification.scheduleStartDate) {
          const startDate = notification.scheduleStartDate.split("T")[0];
          const endDate = notification.scheduleEndDate.split("T")[0];
          setScheduleList([{
            startDate,
            startTime: notification.scheduleStartTime || "",
            endDate,
            endTime: notification.scheduleEndTime || "",
          }]);
        }

        // 5️⃣ Set filters if Filtered mode
        if (notification.filters) {
          setGender(notification.filters.gender || []);
          setInterestedIn(notification.filters.interestedIn || []);
          setMinAge(notification.filters.ageRange?.min || "");
          setMaxAge(notification.filters.ageRange?.max || "");
          setSubscription(notification.filters.subscription || "");
          setBoosted(notification.filters.boosted || "");
          setLocation(notification.filters.location || "");
          // The pocket and readiness come back too: before 2026-11-02 they
          // were dropped here, so Update & Send on a Toronto-only notice
          // went out without its pocket.
          setPocketId(notification.filters.pocketId || "");
          setReadiness(notification.filters.readiness || "");
        } else {
          setGender([]);
          setInterestedIn([]);
          setMinAge("");
          setMaxAge("");
          setSubscription("");
          setBoosted("");
          setLocation("");
          setPocketId("");
          setReadiness("");
        }

      } catch (err) {
        setErrorMessage(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, authToken, isEditMode]);


  const buildFilterQuery = () => {
    const q = new URLSearchParams();
    if (selectType === "Pocket") {
      if (pocketId) q.append("pocketId", pocketId);
      if (readiness) q.append("readiness", readiness);
      return q.toString();
    }
    if (gender && gender.length) q.append("gender", gender.join(","));
    if (interestedIn && interestedIn.length)
      q.append("interestedIn", interestedIn.join(","));
    if (minAge) q.append("minAge", minAge);
    if (maxAge) q.append("maxAge", maxAge);
    if (subscription) q.append("subscription", subscription);
    if (boosted) q.append("boosted", boosted); // add this line
    if (location) q.append("location", location); // ← new
    if (pocketId) q.append("pocketId", pocketId);
    if (readiness) q.append("readiness", readiness);


    return q.toString();
  };

  // fetch all users (used for All & Selected). For Filtered, we use previewFetchUsers.
  const fetchAllUsers = async (autoSelectAll = false) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/notificationUserList?all=true`,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = await response.json();
      const list = data?.users || [];
      setUsersData(list);
      if (autoSelectAll) {
        setSelectUsers(list.map((u) => u._id));
      }
    } catch (err) {
      console.error("fetchAllUsers error:", err);
    } finally {
      setLoading(false);
    }
  };

  const previewFetchUsers = async () => {
    setLoading(true);
    try {
      const query = buildFilterQuery();
      const url =
        `${process.env.REACT_APP_BASE_URL}/admin/notificationUserList` +
        (query ? `?${query}` : "");

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      const data = await response.json();
      const list = data?.users || [];

      setUsersData(list);

      if (list.length === 0) {
        setSelectUsers([]);
        setNoUserMessage("No users found for selected filters.");
      } else {
        setSelectUsers(list.map((u) => u._id));
        setNoUserMessage("");
      }
    } catch (err) {
      console.error("previewFetchUsers error:", err);
      setNoUserMessage("Something went wrong while fetching users.");
    } finally {
      setLoading(false);
    }
  };

  // Audience dropdown handler (2026-08-27): this logic used to live in an
  // effect watching selectType, which also fired when the EDIT loader
  // restored the saved mode — force-selecting every member on an "all"
  // notification and wiping the saved selection on a targeted one. Only a
  // human changing the dropdown runs it now.
  const handleSelectTypeChange = (value) => {
    setSelectType(value);
    // A "No users found" from an earlier preview belongs to that mode; left
    // behind, it hid the Selected Users picker (review 2026-11-02).
    setNoUserMessage("");
    if (value === "all") {
      // All Users is a mode: no chips, no materialized list. The server
      // resolves recipients at send time.
      setUsersData([]);
      setSelectUsers([]);
    } else if (value === "SelectedUser") {
      // fetch list to show in multiselect but don't auto select
      fetchAllUsers(false);
      setSelectUsers([]);
    } else if (value === "Filtered") {
      // keep users empty until 'Preview' clicked
      setUsersData([]);
      setSelectUsers([]);
    } else if (value === "Pocket") {
      // One pocket (founder 2026-11-02): the server resolves the pocket's
      // members at send time; the count below is a preview.
      setUsersData([]);
      setSelectUsers([]);
      setNoUserMessage("");
    }
  };

  // Whenever filter states change, fetch filtered users
  useEffect(() => {
    if (selectType === "Pocket") {
      if (pocketId) {
        previewFetchUsers();
      } else {
        setUsersData([]);
        setSelectUsers([]);
        setNoUserMessage("");
      }
      return;
    }
    if (selectType === "Filtered") {
      // Only fetch if at least one filter is selected (optional)
      if (
        gender.length ||
        interestedIn.length ||
        minAge ||
        maxAge ||
        subscription ||
        boosted ||
        pocketId ||
        readiness
      ) {
        previewFetchUsers();
      } else {
        // Reset if no filters are selected
        setUsersData([]);
        setSelectUsers([]);
        setNoUserMessage("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender, interestedIn, selectType, subscription, boosted, pocketId, readiness]);

  useEffect(() => {
    if (sentType === "Schedule" && scheduleList.length === 0) {
      setScheduleList([
        { startDate: "", endDate: "", startTime: "", endTime: "" },
      ]);
    }
  }, [sentType]);

  useEffect(() => {
    const loadPockets = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/admin/pockets/stats`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        const data = await res.json();
        setPocketOptions(
          (data?.pockets || []).filter((p) => !p.isReviewPocket)
        );
      } catch (err) {
        console.error("Failed to load pockets", err);
      }
    };
    loadPockets();
  }, [authToken]);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const res = await fetchAvailableCities();
        if (res?.data?.status) {
          setCityOptions(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to load cities", err);
      }
    };

    loadCities();
  }, []);

  const clearMessage = () => {
    setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 3000);
  };

  const validateInputs = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!description) newErrors.description = "Description is required";
    if (selectType === "SelectedUser" && selectUsers.length === 0)
      newErrors.selectUsers = "Please select at least one user";
    if (selectType === "Pocket" && !pocketId)
      newErrors.pocketId = "Choose a pocket";
    // For Filtered mode allow zero recipients? If you want to require preview result > 0:
    if (selectType === "Filtered" && selectUsers.length === 0)
      newErrors.selectUsers = "select filtered users before sending";
    if (sentType === "Schedule") {
      const now = new Date();

      scheduleList.forEach((s, i) => {
        const { startDate, startTime, endDate, endTime } = s;

        // Required fields
        if (!startDate) newErrors[`startDate${i}`] = "Start date required";
        if (!startTime) newErrors[`startTime${i}`] = "Start time required";
        if (!endDate) newErrors[`endDate${i}`] = "End date required";
        if (!endTime) newErrors[`endTime${i}`] = "End time required";

        // Only check future/chronology if all fields are filled
        if (startDate && startTime && endDate && endTime) {
          const start = new Date(`${startDate}T${startTime}`);
          const end = new Date(`${endDate}T${endTime}`);

          if (start <= now)
            newErrors[`startDate${i}`] =
              "Start date/time must be in the future";

          if (end <= start)
            newErrors[`endDate${i}`] =
              "End date/time must be after start date/time";
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmitWithType = async (type) => {
    setActionType(type);
    await handleSubmit(null, type);
  };

  const handleSubmit = async (e = null, typeParam = null) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    const currentActionType = typeParam || actionType; // use passed type or state
    if (!validateInputs()) return;

    setLoading(true);
    try {
      // Choose API based on actionType
      const API_ENDPOINT =
        isEditMode
          ? currentActionType === "update"
            ? `${process.env.REACT_APP_BASE_URL}/admin/updateNotificationAdmin/${id}`
            : `${process.env.REACT_APP_BASE_URL}/admin/updateAndSendNotificationAdmin/${id}`
          : `${process.env.REACT_APP_BASE_URL}/admin/sendNotificationAdmin`;

      const method = isEditMode ? "PUT" : "POST";

      const body = {
        title: title.trim(),
        message: description,
        notificationType: "admin_message",
        // The mode travels explicitly; only Selected Users carries a list.
        // "All Users" sends an empty list and the server resolves everyone
        // at send time (2026-08-27).
        audienceType:
          selectType === "all"
            ? "all"
            : selectType === "Filtered" || selectType === "Pocket"
              ? "filtered"
              : "selected",
        targetUsers: selectType === "SelectedUser" ? selectUsers : [],
        sentType,
        scheduleStartDate: scheduleList[0]?.startDate,
        scheduleStartTime: scheduleList[0]?.startTime,
        scheduleEndDate: scheduleList[0]?.endDate,
        scheduleEndTime: scheduleList[0]?.endTime,
        timeZone: userTimeZone,
      };

      if (selectType === "Pocket") {
        // One pocket: the pocket's members, optionally only finished or
        // unfinished profiles. Nothing else filters them.
        body.filters = {
          pocketId,
          readiness: readiness || undefined,
        };
      }

      if (selectType === "Filtered") {
        body.filters = {
          gender: gender.length ? gender : undefined,
          interestedIn: interestedIn.length ? interestedIn : undefined,
          ageRange:
            minAge || maxAge
              ? { min: minAge ? Number(minAge) : undefined, max: maxAge ? Number(maxAge) : undefined }
              : undefined,
          subscription: subscription || undefined,
          boosted: boosted || undefined,
          location: location || undefined,
          pocketId: pocketId || undefined,
          readiness: readiness || undefined,
        };
      }

      const response = await fetch(API_ENDPOINT, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        // setSuccessMessage(
        //   actionType === "update"
        //     ? "Notification updated successfully!"
        //     : "Notification  sent successfully!"
        // );

        let successMsg = "Notification sent successfully!";

        if (isEditMode && currentActionType === "update") {
          successMsg = "Notification updated successfully!";
        } else if (isEditMode && currentActionType === "updateAndSend") {
          successMsg = "Notification updated & sent successfully!";
        }

        setSuccessMessage(successMsg);
        clearMessage();
        setTimeout(() => navigate("/notification"), 3000);
      } else {
        setErrorMessage(data.message || "Something went wrong");
        clearMessage();
      }
    } catch (error) {
      setErrorMessage(error?.message || "Something went wrong");
      clearMessage();
    } finally {
      setLoading(false);
    }
  };


  if (loading) return <Loader />;

  return (
    <>
      {/* Internal CSS */}
      <style>
        {`
    .is-invalid {
      border: 1px solid #dc3545 !important;
      border-radius: 0.375rem;
    }
        `}
      </style>

      <div className="row gy-4">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body">
              {successMessage && <SuccessAlert message={successMessage} />}
              {errorMessage && <DangerAlert message={errorMessage} />}

              <form className="row gy-3" onSubmit={handleSubmit} noValidate>
                {cityNotice && (
                  <div className="col-md-12">
                    <div className="alert alert-info p-3 mb-0">
                      <strong>
                        Sent automatically to {cityNotice.pocketName || "one city's"} members only
                        {sentAt ? `, ${formatEastern(sentAt)} Eastern` : ""}.
                      </strong>{" "}
                      {Number.isFinite(cityNotice.recipients)
                        ? `${cityNotice.recipients} members got it. `
                        : ""}
                      A city notice goes once, so it can't be edited, sent again or deleted.
                      To write to {cityNotice.pocketName || "a city's"} members, use Send
                      Notification and choose One pocket.
                    </div>
                  </div>
                )}
                {/* Title */}
                <div className="col-md-12">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.title ? "is-invalid" : ""
                      }`}
                    value={title}
                    disabled={!!cityNotice}
                    placeholder="Enter Title"
                    onChange={(e) => {
                      setTitle(e.target.value.trimStart());
                      setErrors((prev) => ({ ...prev, title: "" }));
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/^\s+/, "")
                        .replace(/\s{2,}/g, " ");
                    }}
                  />
                  {errors.title && (
                    <p className="text-danger mt-1">{errors.title}</p>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label">
                    Send Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={sentType}
                    disabled={!!cityNotice}
                    onChange={(e) => setSentType(e.target.value)}
                  >
                    <option value="" disabled>
                      --Select Send Type--
                    </option>
                    <option value="Immediate">Send Now (Instant)</option>
                    <option value="Schedule">Schedule Notification</option>
                  </select>
                </div>

                {/* Schedule DateTime */}
                {sentType === "Schedule" && (
                  <div className="col-md-12">
                    {scheduleList.map((item, index) => (
                      <div key={index} className="row g-3 mb-3">
                        <div className="col-md-3">
                          <label className="form-label">Start Date</label>
                          <input
                            type="date"
                            className={`form-control ${errors[`startDate${index}`] ? "is-invalid" : ""
                              }`}
                            value={item.startDate || ""}
                            onChange={(e) => {
                              const arr = [...scheduleList];
                              arr[index] = {
                                ...arr[index],
                                startDate: e.target.value,
                              };
                              setScheduleList(arr);
                            }}
                          />
                          {errors[`startDate${index}`] && (
                            <p className="text-danger mt-1">
                              {errors[`startDate${index}`]}
                            </p>
                          )}
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">End Date</label>
                          <input
                            type="date"
                            className={`form-control ${errors[`endDate${index}`] ? "is-invalid" : ""
                              }`}
                            value={item.endDate || ""}
                            onChange={(e) => {
                              const arr = [...scheduleList];
                              arr[index] = {
                                ...arr[index],
                                endDate: e.target.value,
                              };
                              setScheduleList(arr);
                            }}
                          />
                          {errors[`endDate${index}`] && (
                            <p className="text-danger mt-1">
                              {errors[`endDate${index}`]}
                            </p>
                          )}
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">Start Time</label>
                          <input
                            type="time"
                            className={`form-control ${errors[`startTime${index}`] ? "is-invalid" : ""
                              }`}
                            value={item.startTime || ""}
                            onChange={(e) => {
                              const arr = [...scheduleList];
                              arr[index] = {
                                ...arr[index],
                                startTime: e.target.value,
                              };
                              setScheduleList(arr);
                            }}
                          />
                          {errors[`startTime${index}`] && (
                            <p className="text-danger mt-1">
                              {errors[`startTime${index}`]}
                            </p>
                          )}
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">End Time</label>
                          <input
                            type="time"
                            className={`form-control ${errors[`endTime${index}`] ? "is-invalid" : ""
                              }`}
                            value={item.endTime || ""}
                            onChange={(e) => {
                              const arr = [...scheduleList];
                              arr[index] = {
                                ...arr[index],
                                endTime: e.target.value,
                              };
                              setScheduleList(arr);
                            }}
                          />
                          {errors[`endTime${index}`] && (
                            <p className="text-danger mt-1">
                              {errors[`endTime${index}`]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Select Type */}
                <div className="col-md-12">
                  <label className="form-label">
                    Select Users <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={selectType}
                    disabled={!!cityNotice}
                    onChange={(e) => {
                      handleSelectTypeChange(e.target.value);
                    }}
                  >
                    <option value="" disabled>
                      -- Select User Type --
                    </option>
                    <option value="all">All Users</option>
                    <option value="Pocket">One pocket</option>
                    <option value="SelectedUser">Selected Users</option>
                    <option value="Filtered">Filtered Users</option>
                  </select>
                </div>

                {/* One pocket (founder 2026-11-02): one city's members, and
                    optionally only finished or unfinished profiles. */}
                {selectType === "Pocket" && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">
                        Pocket <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-control ${errors.pocketId ? "is-invalid" : ""}`}
                        value={pocketId}
                        disabled={!!cityNotice}
                        onChange={(e) => {
                          setPocketId(e.target.value);
                          setErrors((prev) => ({ ...prev, pocketId: "" }));
                        }}
                      >
                        <option value="">-- Choose a pocket --</option>
                        {pocketOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.status ? ` (${p.status})` : ""}
                          </option>
                        ))}
                      </select>
                      {errors.pocketId && (
                        <p className="text-danger mt-1">{errors.pocketId}</p>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Member readiness</label>
                      <select
                        className="form-control"
                        value={readiness}
                        disabled={!!cityNotice}
                        onChange={(e) => setReadiness(e.target.value)}
                      >
                        <option value="">Everyone in the pocket</option>
                        <option value="ready">Ready to be introduced</option>
                        <option value="incomplete">Still finishing onboarding</option>
                      </select>
                    </div>

                    {pocketId && !cityNotice && (
                      <div className="col-md-12">
                        <div className="alert alert-info p-2 mb-0">
                          {noUserMessage
                            ? noUserMessage
                            : `About ${users.length} ${pocketName(pocketOptions, pocketId)} members right now. Only members of this pocket get it.`}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Filter controls (only when Filtered selected) */}
                {selectType === "Filtered" && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">Launch pocket</label>
                      <select
                        className="form-control"
                        value={pocketId}
                        onChange={(e) => setPocketId(e.target.value)}
                      >
                        <option value="">All pockets</option>
                        {pocketOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.status ? ` (${p.status})` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Member readiness</label>
                      <select
                        className="form-control"
                        value={readiness}
                        onChange={(e) => setReadiness(e.target.value)}
                      >
                        <option value="">Everyone</option>
                        <option value="ready">
                          Ready to be introduced
                        </option>
                        <option value="incomplete">
                          Still finishing onboarding
                        </option>
                      </select>
                    </div>

                    <div className="col-md-4 d-flex align-items-end gap-2">
                      <div className="flex-grow-1">
                        <label className="form-label">Location</label>
                        <select
                          className="form-control"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                        >
                          <option value="">All Cities</option>
                          {cityOptions.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>

                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={previewFetchUsers} // fetch filtered users when clicked
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4 d-flex align-items-end gap-2">
                      <div className="flex-grow-1">
                        <label className="form-label">Age Range</label>

                        <div className="d-flex gap-2">
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            placeholder="Min age"
                            value={minAge}
                            onChange={(e) => setMinAge(e.target.value)}
                          />

                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            placeholder="Max age"
                            value={maxAge}
                            onChange={(e) => setMaxAge(e.target.value)}
                          />
                        </div>
                      </div>

                      <div>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={previewFetchUsers}
                        >
                          Preview
                        </button>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Gender</label>
                      <Multiselect
                        options={genderOptions}
                        displayValue="name"
                        onSelect={(selected) =>
                          setGender(selected.map((s) => s.id))
                        }
                        onRemove={(selected) =>
                          setGender(selected.map((s) => s.id))
                        }
                        selectedValues={genderOptions.filter((o) =>
                          gender.includes(o.id)
                        )}
                        placeholder="Select genders"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Interested In</label>
                      <Multiselect
                        options={interestedOptions}
                        displayValue="name"
                        onSelect={(selected) =>
                          setInterestedIn(selected.map((s) => s.id))
                        }
                        onRemove={(selected) =>
                          setInterestedIn(selected.map((s) => s.id))
                        }
                        selectedValues={interestedOptions.filter((o) =>
                          interestedIn.includes(o.id)
                        )}
                        placeholder="Select interests"
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Subscription</label>
                      <select
                        className="form-control"
                        value={subscription}
                        onChange={(e) => setSubscription(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="active">Active</option>
                        <option value="premium">Premium</option>
                        <option value="expired">Expired</option>
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Boosted Status</label>
                      <select
                        className="form-control"
                        value={boosted}
                        onChange={(e) => setBoosted(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="true">Boosted</option>
                        <option value="false">Not Boosted</option>
                      </select>
                    </div>
                  </>
                )}

                {/* All Users is a mode, not a thousand chips: show what
                    will happen instead of materializing every member. */}
                {selectType === "all" && (
                  <div className="col-md-12 mt-3">
                    <div className="alert alert-info p-2 mb-0">
                      This notification goes to every active member at send
                      time. No individual selection needed.
                    </div>
                  </div>
                )}

                {/* Multiselect for manual selection (appears for SelectedUser & Filtered preview results) */}
                {selectType !== "all" && selectType !== "Pocket" && (
                <div className="col-md-12 mt-3">
                  <label className="form-label">Users (preview / select)</label>
                  {noUserMessage ? (
                    <div className="alert alert-warning p-2">
                      {noUserMessage}
                    </div>
                  ) : (
                    <div>
                      <Multiselect
                        className={errors.selectUsers ? "is-invalid" : ""}
                        options={users.map((u) => ({
                          name: u.email || u.fullName || "Unknown User",
                          id: u._id,
                        }))}
                        selectedValues={users
                          .filter((u) => selectUsers.includes(u._id))
                          .map((u) => ({
                            name: u.email || u.fullName || "Unknown User",
                            id: u._id,
                          }))}
                        onSelect={(selected) =>
                          setSelectUsers(selected.map((u) => u.id))
                        }
                        onRemove={(selected) =>
                          setSelectUsers(selected.map((u) => u.id))
                        }
                        displayValue="name"
                      />
                    </div>
                  )}
                  {errors.selectUsers && (
                    <p className="text-danger mt-1">{errors.selectUsers}</p>
                  )}
                </div>
                )}

                {/* Description */}
                <div className="col-md-12">
                  <label className="form-label">
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.description ? "is-invalid" : ""
                      }`}
                    rows="5"
                    value={description}
                    disabled={!!cityNotice}
                    placeholder="Enter Description"
                    onChange={(e) => {
                      setDescription(e.target.value.trimStart());
                      setErrors((prev) => ({ ...prev, description: "" }));
                    }}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/^\s+/, "")
                        .replace(/\s{2,}/g, " ");
                    }}
                  />
                  {errors.description && (
                    <p className="text-danger mt-1">{errors.description}</p>
                  )}
                </div>

                {/* <div className="col-12">

                  <button className="btn btn-primary mt-3 float-end" type="submit">
                    {loading ? "Saving..." : isEditMode ? "Update Notification" : "Send Notification"}
                  </button>
                </div> */}
                <div className="col-12 d-flex justify-content-end gap-2">
                  {cityNotice ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate("/notification")}
                    >
                      Back to notifications
                    </button>
                  ) : isEditMode ? (<>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleSubmitWithType("update")}
                      disabled={loading}
                    >
                      {loading && actionType === "update" ? "Saving..." : "Update"}
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleSubmitWithType("updateAndSend")}
                      disabled={loading}
                    >
                      {loading && actionType === "updateAndSend" ? "Sending..." : "Update & Send"}
                    </button>

                  </>) : (<>
                    <button className="btn btn-primary mt-3 float-end" type="submit">
                      {loading ? "Saving..." : "Send Notification"}
                    </button>
                  </>)}

                </div>

              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SendNotificationNew;
